import React from 'react';
import styled, { css } from 'styled-components';
import classNames from 'classnames';
import { NavLink } from 'react-router-dom';
import materialIcons from '../../icon/constants/material-icons-constants';

// Components
import Icon, { IconAnimatedChevron } from '../../icon';
import { H2 } from '../../title';

// styling
import { colors } from '../../../style';
import { Flex } from '../../box';

const variants = {
  default: css`
    color: ${colors.gray900};
    &:hover {
      background: ${colors.gray100};
    }
    &.active {
      color: ${props => props.theme.actionColor};
      background: ${props => props.theme.actionLight};
    }
    &:focus-visible {
      border: 2px solid ${colors.sky};
    }
  `,
  'action-light': css`
    color: ${colors.gray900};
    &:hover {
      background: ${colors.gray100};
    }
    &.active,
    &.active:hover {
      color: ${colors.gray900};
      background: ${props => props.theme.actionLight};
    }
    &:focus-visible {
      border: 2px solid ${colors.sun};
    }
  `,
  'selected-blue': css`
    color: ${colors.gray100};
    &:hover {
      background: ${colors.selectedBlue};
    }
    &.active {
      background: ${colors.selectedBlue};
    }
    &:focus-visible {
      border: 2px solid ${colors.sun};
    }
  `,
};
const SideMenuItem = styled(
  ({
    label,
    variant,
    disabled,
    onClick,
    className,
    active,
    outlined,
    isOpen: isOpenProp = true,
    icon,
    children,
    disableRoute,
    componentClass: Component = NavLink,
    ...rest
  }: {
    label?: string | React.ReactNode;
    variant?: keyof typeof variants;
    disabled?: boolean;
    onClick?: (e: React.MouseEvent<HTMLElement>) => void;
    isOpen?: boolean;
    icon?: keyof typeof materialIcons;
    to?: string;
    outlined?: boolean;
    disableRoute?: boolean;
    children?: React.ReactNode;
    active?: boolean;
    className?: string;
    componentClass?: 'button' | React.ElementType;
  }) => {
    const [isOpen, setIsOpen] = React.useState(isOpenProp);
    return (
      <React.Fragment>
        <Component
          aria-expanded={!children ? undefined : isOpen}
          disabled={disabled}
          variant={variant}
          className={classNames(className, { active })}
          onClick={(e: any) => {
            if (disableRoute) {
              e.preventDefault();
            }
            if (!disabled) {
              onClick?.(e);
            }
            setIsOpen(!isOpen);
          }}
          {...rest}
        >
          <Flex>
            {icon && (
              <Flex marginRight={'xs'}>
                <Icon size={'xxl'} outlined={outlined} variant={'gray600'} name={icon} />
              </Flex>
            )}
            <H2 size={'sm'} marginBottom={'none'} marginTop={'none'} variant={'inherit'}>
              {label}
            </H2>
          </Flex>
          {!disabled && children && <IconAnimatedChevron size={'xl'} isOn={isOpen} />}
        </Component>
        {isOpen ? children : null}
      </React.Fragment>
    );
  },
)`
  padding: 16px 8px 16px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  ${props => variants[props.variant || 'default']};

  &.disabled,
  &[disabled] {
    background: ${colors.gray600};
    cursor: not-allowed;
  }
`;

export default SideMenuItem;
